<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="900px"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        color="hrissecondary"
        block
        class="white--text"
        v-on="on"
      >
        Create Claim Assignee
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Claim Assignee"
      text="Create Claim Assignee"
      class="mb-0"
    > 
      <v-container v-if="dialog">
        <v-row
          v-if="!loading"
          no-gutters
        >
          <v-col cols="12">
            <v-text-field
              v-model="approvalLevel"
              v-validate="'required|min_value:1|cannotBeSuperiorLevel|maxValue'"
              :min="1"
              :max="claimApprovalWorkflowBatch.total_level_of_approval"
              :error-messages="errors.collect('approvalLevel')"
              type="number"
              data-vv-name="approvalLevel"
              data-vv-as="approval level"
              label="Approval Level"
              @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
            />

            <v-autocomplete
              v-model="assignee"
              v-validate="'required'"
              :error-messages="errors.collect('assignee')"
              data-vv-name="assignee"  
              data-vv-as="employee"  
              :items="filteredEmployees"
              label="Employee"
              item-text="person.name"
              item-value="employee_uid"
            />
          </v-col>
          
          <v-col
            cols="12"
            class="py-0"
          >
            <v-checkbox
              v-model="enableThreshold"
              color="green"
              label="Enable threshold?"
            />
            <div class="text-caption text--secondary ml-8 mt-n6">
              By enabling threshold, you are required to set the threshold rule. This approver will only apply when one of the claim request entry meet one of the threshold rules.
            </div>
          </v-col>

          <v-col
            v-if="enableThreshold"
            cols="11"
            class="mx-8 my-2"
          >
            <v-row>
              <v-col>
                <span class="font-weight-bold pr-2">Threshold Rule:</span>
                <v-tooltip
                  v-if="rules.length < types.length"
                  bottom
                >
                  <template v-slot:activator="{ on: tooltip }">
                    <v-icon
                      v-if="rules.length < types.length"
                      color="primary"
                      v-on="{ ...dialog, ...tooltip }"
                      @click="addRow"
                    >
                      mdi-plus
                    </v-icon>
                  </template>
                  <span>Add new rule</span>
                </v-tooltip>
              </v-col>
            </v-row>

            <v-row
              v-for="(rule,counter) in rules"
              :key="counter"
            >
              <v-col
                cols="3"
                class="py-0"
              >
                <v-autocomplete
                  v-model="rule.type"
                  v-validate="'required'"
                  :name="'type' + counter"
                  :error-messages="errors.collect('type' + counter)"
                  :items="thresholdTypes(counter)"
                  data-vv-delay="500"
                  data-vv-as="rule"
                  placeholder="Type"
                  autocomplete="off"
                  @change="setUnit(rule)"
                />
              </v-col>
              <v-col
                cols="5"
                class="py-0"
              >
                <v-text-field
                  v-model="rule.threshold_value"
                  v-validate="'required|min_value:0'"
                  :min="0"
                  :name="'threshold_value' + counter"
                  :error-messages="errors.collect('threshold_value' + counter)"
                  data-vv-delay="500"
                  data-vv-as="value"
                  label="Value"
                  autocomplete="off"
                  type="number"
                  :prefix="rule.type === 'Amount' ? 'is more than RM ' : 'is more than '"
                  @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
                />
              </v-col>
              <v-col
                cols="2"
                class="py-0"
              >
                <v-text-field
                  v-model="rule.threshold_unit"
                  v-validate="'required'"
                  :name="'threshold_unit' + counter"
                  :error-messages="errors.collect('threshold_unit' + counter)"
                  data-vv-delay="500"
                  data-vv-as="unit"
                  label="Unit"
                  readonly
                  disabled
                />
              </v-col>

              <v-col
                cols="2"
                class="text-center"
              >
                <v-icon
                  color="error"
                  @click="deleteRow(counter)"
                >
                  mdi-window-close
                </v-icon>
              </v-col>
            </v-row>
          </v-col>
   
          <v-row>
            <v-col>
              <v-autocomplete
                v-model="selectedCompanies"
                v-validate="'required'"
                :loading="loading"
                label="Companies"
                item-text="name"
                item-value="id"
                :items="companies"
                clearable
                multiple
                hide-selected 
                chips
                small-chips
                deletable-chips
                :error-messages="errors.collect('selectedCompanies')"
                data-vv-name="selectedCompanies"  
                data-vv-as="companies"
              />
            </v-col>
          </v-row>
          
          <v-col
            cols="12"
            class="py-0"
          >
            <v-checkbox
              v-model="isFinalApprover"
              color="green"
              label="Is final approver?"
            />
            <div class="text-caption text--secondary ml-8 mt-n6">
              Check this box to designate this claim assignee as the final approver for the assigned companies.
            </div>
          </v-col>
        </v-row>
        <v-row
          v-if="loading" 
          class="fill-height"
          align="center" 
          justify="center"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
        </v-row>
        
        <v-card-actions v-if="dialog && !loading">
          <v-spacer />
          <v-btn
            :disabled="progressing"
            text
            class="px-10"
            @click="dialog = false"
          >
            Close
          </v-btn>
          <v-btn
            :loading="progressing"
            color="hrissecondary"   
            class="white--text px-10"       
            @click="submitForm"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-container>
    </material-card>
  </v-dialog>
</template>

<script>
  import { mapState } from 'vuex'
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      companies: {
        type: Array,
        required: true,
      },
      departments: {
        type: Array,
        required: true,
      },
      employees: {
        type: Array,
        required: true,
      },
      claimApprovalWorkflowBatch: {
        type: Object,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,        
        loading: false,
        progressing: false,
        approvalLevel: null,
        enableThreshold: 0,
        isFinalApprover: 0,
        rules: [],
        filteredEmployees: [],
        selectedCompanies: [],
        assignee: null,
        workflow: null,
        types: ['Amount', 'Date validity'],
      }
    },
    computed: {  
      ...mapState({ employee: 'currentEmployee' }),
      thresholdTypes () {
        return (counter) => {
          const selectedTypes = this.rules
            .map((rule, item) => item !== counter ? rule.type : null)
            .filter(type => type)

          return this.types.filter(type => !selectedTypes.includes(type))
        }
      },
    },
    watch: {
      dialog (value) {
        if (value) {
          let scope = this
          this.$validator.extend('cannotBeSuperiorLevel', {
            getMessage () {
              return 'Cannot enter same level as superior\'s approval level'
            },
            validate (value) {
              const numValue = Number(value)
              const maxLevel = Number(scope.claimApprovalWorkflowBatch.superior_approval_level)

              return numValue !== maxLevel
            },
          })
          
          this.$validator.extend('maxValue', {
            getMessage () {
              return 'Cannot enter level more than current workflow batch maximum approval level'
            },
            validate (value) {
              const numValue = Number(value)
              const maxLevel = Number(scope.claimApprovalWorkflowBatch.total_level_of_approval)

              return numValue <= maxLevel
            },
          })
        } else {
          this.$resetData()
        }
      },
      enableThreshold: {
        immediate: true,
        handler (value) {
          if (value) {
            if (this.rules.length === 0) {
              this.addRow()
            }
          } else {
            this.rules = []
          }
        },
      },
      approvalLevel: {
        immediate: true,
        handler (value) {
          if (
            value && (Number(value) !== Number(this.claimApprovalWorkflowBatch.superior_approval_level)) && 
            (Number(value) <= Number(this.claimApprovalWorkflowBatch.total_level_of_approval))
          ) {
            this.workflow = this.claimApprovalWorkflowBatch.claim_approval_workflows.find(workflow => {
              return Number(workflow.approval_level) === Number(value)
            })

            if (this.workflow.approvable_type === 'App\\Model\\Department') {
              this.filteredEmployees = this.employees.filter(employee => { 
                return Number(employee.department_id) === Number(this.workflow.approvable_id)
              })
            } else if (this.workflow.approvable_type === 'App\\Model\\Position') {
              this.filteredEmployees = this.employees.filter(employee => { 
                return Number(employee.current_position.position_id) === Number(this.workflow.approvable_id)
              })
            } else { // specific employee
              this.filteredEmployees = this.employees.filter(employee => { 
                return employee.employee_uid === this.workflow.approvable_id
              })

              this.assignee = this.filteredEmployees[0].employee_uid
            }
          } else {
            this.filteredEmployees = []
          }
        },
      },
      selectedCompanies (newVal) {
        const last = newVal[newVal.length - 1]

        // If last selected is 'ALL', keep only 'ALL'
        if (last === 'ALL' && newVal.length > 1) {
          // Replace with only 'ALL' after current update cycle
          this.$nextTick(() => {
            this.selectedCompanies = ['ALL']
          })
        } else if (newVal.includes('ALL') && newVal.length > 1) {
          // Remove 'ALL' if other company is selected after it
          this.$nextTick(() => {
            this.selectedCompanies = newVal.filter(id => id !== 'ALL')
          })
        }
      },
    },
    methods: {
      setUnit (rule) {
        if (rule.type === 'Amount') {
          rule.threshold_unit = 'RM'
        } else {
          rule.threshold_unit = 'days'
        }
      },
      addRow () {
        this.rules.push({ type: null, threshold_value: null, threshold_unit: '' })
      },
      deleteRow (counter) {
        this.rules.splice(counter, 1)
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 

            this.progressing = true
    
            this.$api.claimAssignees().create(this.prepareFormData())
              .then((res) => {
                if (res.status === 200 || res.status === 201) {
                  this.$emit('created')              
                  this.$snack({ message: 'Added new claim assignee' })
                  this.$resetData()
                  this.dialog = false                 
                }
              })
              .catch((err) => {
                if (err.response.status === 422) {
                  if (this.enableThreshold && this.rules.length <= 0) {
                    this.addRow()
                  }

                  this.$snack({ 
                    color: 'error',
                    message: err.response.data.message, 
                  })
                } else {
                  this.$snack({
                    color: 'error',
                    message: 'Failed to create new claim assignee',
                  })
                }
              })
              .then(() => {
                this.progressing = false
              })
          })
      },
      prepareFormData () {
        return {
          approval_level: this.approvalLevel,
          employee_uid: this.assignee,
          claim_approval_workflow_id: this.workflow.id,
          enable_threshold: this.enableThreshold,
          is_final_approver: this.isFinalApprover,
          rules: this.rules,
          companies: this.selectedCompanies.includes('ALL') ? (
            this.companies.filter(company => {
              return company.id !== 'ALL'
            }).map(company => {
              return company.id
            })
          ) : this.selectedCompanies,
        }
      },
    },
  }
</script>
